import React, { useState } from 'react';
import PageWrapper from '../../components/wrapper/PageWrapper';
import { Typo } from '../../components/Typo';
import Address from '../../components/shared/Address';
import Button from '../../components/shared/Button';
import ShareIcon from '../../components/icons/ShareIcon';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDateOnly } from '../../i18n/format';
import { TRANSLATED_CONSTANTS } from '../../static/translatedConstants';
import Divider from '../../components/shared/Divider';
import AccountInfo from '../../components/shared/AccountInfo';
import DisLikeIcon from '../../components/icons/DisLikeIcon';
import LikeIcon from '../../components/icons/LikeButton';
import ReportButton from '../../components/shared/ReportButton';
import DirectionButton from './DirectionButton';
import { useGetStorefront } from '../../hooks/storfront/useGetStorefront';
import { useSubmitClaim, useMyClaimRequests } from '../../hooks/spot/useClaim';
import { useStorefrontReviews } from '../../hooks/storfront/useStorefrontReviews';
import { useSubmitReview } from '../../hooks/storfront/useSubmitReview';
import { AuthUserState } from '../../context/AuthUserProvider';
import { useParams } from 'react-router-dom';
import Loader from '../../components/shared/Loader';
import OperationHourLabel from '../../components/shared/Hours Operation/OperationHourLabel';
import StorefrontItems from './StorefrontItems';
import ReviewList from '../../components/shared/trust/ReviewList';
import ReviewModal from '../../components/shared/trust/ReviewModal';
import SocialChips from '../../components/shared/trust/SocialChips';
import ImageSlider from '../../components/shared/ImageSlider/ImageSlider';
import { Image } from '../../model/Image';
import Location from '../../components/shared/map/Location';
import { getCurrentDate } from '../../util/CurrentDate';
import { useReportStorefront } from '../../hooks/listing/useReportStorefront';
import { useNavigate } from 'react-router-dom';
import { useHandleSave } from '../../hooks/useHandleSave';
import { useCheckOnlineUsers } from '../../hooks/useCheckOnlineUsers';
import { useDispatch } from 'react-redux';
import { showModal } from '../../lib/redux/reducers/modal.slice';
import ReportModal from '../../components/modal/ReportModal';
import { setDirectionData } from '../../lib/redux/reducers/direction.slice';
import { setShareData } from '../../lib/redux/reducers/share.slice';
import { ROLES, ROUTES } from '../../constants/data';
import { useHandleUserDecisions } from '../../hooks/listing/useHandelUserDecisions';
import { addNotification } from '../../lib/redux/reducers/notification.slice';
import { useAppSelector } from '../../lib/redux/hooks';
import PrimaryActionModal from '../../components/modal/PrimaryActionModal';
import TrashIcon from '../../components/icons/TrashIcon';
import MessageIcon from '../../components/icons/MessageIcon';
import { useWarnDelete } from '../../hooks/reports/useWarnDelete';
import { useGetUserRole } from '../../hooks/useRole';
import { setChatData, setProfileData, showSlide } from '../../lib/redux/reducers/slide.slice';
import PageSeo from '../../components/shared/PageSeo';

// Show just the domain for a shop's website link/CTA - no protocol, no "www.",
// no path or query (the href still points at the full URL). Falls back to a
// simple strip if the value somehow isn't a parseable URL.
const hostnameOf = (raw?: string | null): string => {
  const s = (raw || '').trim();
  if (!s) return '';
  try {
    const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`);
    return u.hostname.replace(/^www\./i, '');
  } catch {
    return s
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split('/')[0];
  }
};

const StorefrontDetails = () => {
  const dispatch = useDispatch();
  const [showReportListingModal, setShowReportListingModal] = useState(false);
  const [showWarnAndDeleteModal, setShowWarnAndDeleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const reportStorefront = useReportStorefront();
  const handleUserDecisions = useHandleUserDecisions();
  const notifications = useAppSelector((state) => state.notifications.notifications);
  const navigate = useNavigate();
  const auth = AuthUserState();
  const user = auth.authUser;
  const intl = useIntl();
  const { id } = useParams();
  const { storefront, loading, refetch } = useGetStorefront(Number(id), user?.id || null, -1);
  const { average, total, histogram, reviews, refetch: refetchReviews } = useStorefrontReviews(id);
  const { submitReview, submitting } = useSubmitReview();
  const { save } = useHandleSave(refetch);
  const warnDelete = useWarnDelete();
  const storefrontUser = storefront?.user;
  // Seed the Map (Doge Spotters): unclaimed community pins have no owner - show a
  // "community-added" identity + a claim path instead of an empty seller card.
  const isCommunitySpot = storefront?.claimStatus === 'unclaimed';
  // Claimed, but the new owner hasn't finished setup yet (no image saved - the
  // editor can't save without one). Public-facing "being set up" state; flips
  // off automatically once they complete and save.
  const isSettingUp =
    storefront?.claimStatus === 'claimed' &&
    !(Array.isArray(storefront?.image) && storefront.image.length > 0);
  const communitySpotter =
    storefront?.communityReporter?.username || storefront?.communityReporter?.name || '';
  const communityVerifiedOn = storefront?.verifiedAt
    ? formatDateOnly(new Date(storefront.verifiedAt), {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    : '';
  // Slice D: the real in-app claim flow (replaces the interim mailto). The
  // backend resolver owns validation and the pending-claim caps; this form
  // only mirrors the required-fields rule to keep the submit button honest.
  const submitClaim = useSubmitClaim();
  const { claims: myClaims, claimsRefetch } = useMyClaimRequests(!(isCommunitySpot && user));
  const myPendingClaim = myClaims.find(
    (c) => String(c.storefrontId) === String(storefront?.id) && c.status === 'pending'
  );
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimDone, setClaimDone] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimRole, setClaimRole] = useState('');
  const [claimEmail, setClaimEmail] = useState('');
  const [claimPhone, setClaimPhone] = useState('');
  const [claimProofType, setClaimProofType] = useState('domain_email');
  const [claimProofUrl, setClaimProofUrl] = useState('');
  const [claimProofNote, setClaimProofNote] = useState('');
  const claimFormValid =
    claimRole.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(claimEmail.trim()) &&
    (claimProofUrl.trim().length > 0 || claimProofNote.trim().length >= 10);

  const claimReasonMessage = (reason?: string | null) => {
    switch (reason) {
      case 'email-not-verified':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_email_verify',
          defaultMessage: 'Verify your email first, then claim your business.'
        });
      case 'already-pending':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_already_pending',
          defaultMessage: 'You already have a pending claim for this business.'
        });
      case 'too-many-pending':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_too_many',
          defaultMessage: 'You have too many pending claims. Please wait for a review first.'
        });
      case 'not-found':
      case 'not-claimable':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_not_claimable',
          defaultMessage: "This business can't be claimed right now."
        });
      case 'need-proof':
      case 'bad-proof-url':
      case 'bad-proof-type':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_need_proof',
          defaultMessage: 'Add a proof link or a short note so we can verify you.'
        });
      case 'bad-email':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_bad_email',
          defaultMessage: 'Enter a valid business contact email.'
        });
      case 'bad-role':
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_bad_role',
          defaultMessage: 'Tell us your role at the business.'
        });
      default:
        return intl.formatMessage({
          id: 'page.storefront.claim_reason_generic',
          defaultMessage: 'Could not submit the claim. Please check the form and try again.'
        });
    }
  };

  const onClaimClick = () => {
    if (!user) {
      dispatch(showModal('signup'));
      return;
    }
    setClaimError(null);
    setClaimOpen(true);
  };

  const onClaimSubmit = async () => {
    if (!claimFormValid || claimBusy) {
      return;
    }
    setClaimBusy(true);
    setClaimError(null);
    try {
      const res = await submitClaim({
        variables: {
          storefrontId: String(storefront?.id),
          roleAtBusiness: claimRole.trim(),
          contactEmail: claimEmail.trim(),
          contactPhone: claimPhone.trim() || undefined,
          proofType: claimProofType,
          proofUrl: claimProofUrl.trim() || undefined,
          proofNote: claimProofNote.trim() || undefined
        }
      });
      const r = res?.data?.submitClaimRequest;
      if (r?.ok) {
        setClaimDone(true);
        setClaimOpen(false);
        claimsRefetch();
      } else {
        setClaimError(claimReasonMessage(r?.reason));
      }
    } catch (e) {
      setClaimError(claimReasonMessage(null));
    } finally {
      setClaimBusy(false);
    }
  };
  // W-D (three-intent): how buyers order from this shop; drives the hero CTA.
  const sellingMode = storefront?.sellingMode as string | undefined;
  // W-D: a website shop is bought off-site, so the in-person "meet up / open hours"
  // framing doesn't apply to it. Reframe those bits for website mode only;
  // dogelist + in_person keep the meet-up wording.
  const isWebsiteShop = sellingMode === 'website';
  const onAskQuestion = () => {
    // Community-reported pins have no owner to message.
    if (!storefrontUser?.id) return;
    if (user && user.id !== storefrontUser?.id && user.profileCompleted) {
      dispatch(
        setChatData({
          sender: {
            profileImage: { url: user.profileImage?.url },
            name: user.name,
            username: user.username,
            id: user.id
          },
          receiver: storefrontUser
        })
      );
      dispatch(showSlide('message'));
    } else if (!user) {
      dispatch(showModal('login'));
    } else if (user && !user.profileCompleted) {
      dispatch(showModal('completeAccountToSendMessage'));
    }
  };
  const isUserOnline = useCheckOnlineUsers(Number(storefrontUser?.id));
  const profileState = useAppSelector((state) => state.slide.profile);

  const role = useGetUserRole();

  const myReview = user ? reviews.find((r: any) => r.authorId === String(user.id)) : null;
  const isOwner = user?.id == storefront?.user?.id;

  const onLeaveReview = () => {
    if (!user) {
      dispatch(showModal('login'));
      return;
    }
    setShowReviewModal(true);
  };

  const onSubmitReview = async (rating: number, comment: string) => {
    try {
      await submitReview({ variables: { input: { storefrontId: id, rating, comment } } });
      setShowReviewModal(false);
      refetchReviews();
    } catch {
      // Leave the modal open so the user can retry.
    }
  };

  const onSave = async (liked: boolean, savedId: number | undefined) => {
    save(storefront?.id, 'storefront', liked, savedId);
  };

  const onCloseReportModal = (value: boolean) => {
    setShowReportListingModal(value);
  };

  const onReport = async (detail: string, claim: string) => {
    await reportStorefront({
      variables: {
        data: {
          detail: detail,
          claim: claim,
          store_front: Number(storefront?.id) || 0,
          reporter: user?.id,
          date: new Date(),
          publishedAt: getCurrentDate()
        }
      }
    }).then(async (response: any) => {
      if (response) {
        await handleUserDecisions.changeDecisions(user?.id, user?.decisions + 1);
        dispatch(
          addNotification({
            id:
              notifications && notifications?.length > 0
                ? notifications[notifications?.length - 1]?.id + 1
                : 1,
            title: intl.formatMessage({
              id: TRANSLATED_CONSTANTS.STOREFRONT_REPORT_ALERT_TITLE.id,
              defaultMessage: TRANSLATED_CONSTANTS.STOREFRONT_REPORT_ALERT_TITLE.defaultMessage
            }),
            description: intl.formatMessage({
              id: TRANSLATED_CONSTANTS.LISTING_REPORT_ALERT_DESCRIPTION.id,
              defaultMessage: TRANSLATED_CONSTANTS.LISTING_REPORT_ALERT_DESCRIPTION.defaultMessage
            }),
            type: 'flag',
            linkText: '',
            link: '',
            linkIcon: false
          })
        );
        setShowReportListingModal(false);
        navigate(-1);
      }
    });
  };

  const handleReportButtonClicked = () => {
    if (!user) {
      dispatch(showModal('login'));
    } else {
      setShowReportListingModal(true);
    }
  };

  const onGetDirection = () => {
    dispatch(showModal('direction'));
    dispatch(
      setDirectionData({
        current: {
          lat: user?.location?.latitude,
          long: user?.location?.longitude
        },
        destination: {
          lat: storefront?.location?.latitude,
          long: storefront?.location?.longitude
        }
      })
    );
  };

  const onShare = () => {
    dispatch(
      setShareData({ isOpen: true, url: `${ROUTES.STOREFRONT_DETAILS_ROUTE}/${storefront?.id}` })
    );
  };

  const onCloseWarnAndDeleteModal = async (confirm: boolean) => {
    if (!confirm) {
      setShowWarnAndDeleteModal(false);
      return;
    }
    // Community-reported pins have no owner to warn (storefrontUser is null);
    // guard so this admin action can't crash on an unclaimed spot.
    if (!storefrontUser?.id) {
      setShowWarnAndDeleteModal(false);
      return;
    }
    await warnDelete({
      warnedId: storefrontUser?.id,
      warnerId: user?.id,
      storefrontId: storefront?.id,
      warnUserEmail: storefrontUser?.email,
      userName: storefrontUser?.username,
      claim: '',
      detail: ''
    }).then(() => {
      navigate(-1);
    });
    setShowWarnAndDeleteModal(false);
  };

  // Dormant shop (account-type switch): the public page is a quiet "closed"
  // card - URL stays warm, meta-server adds noindex. The owner still gets the
  // full page (with a banner below) so nothing feels lost.
  const isClosed = !loading && storefront && storefront.available === false;
  if (isClosed && user?.id != storefront?.user?.id) {
    return (
      <div>
        <PageSeo title={storefront?.name} />
        <PageWrapper hasSpeicifWidth={true}>
          <div className="mx-auto flex min-h-[40vh] max-w-md flex-col items-center justify-center gap-4 py-24 text-center">
            {storefront?.logo?.url ? (
              <img
                src={storefront?.logo?.url}
                alt={storefront?.name}
                className="h-16 w-16 rounded-full border border-gray-200 object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-2xl font-semibold text-gray-500">
                {(storefront?.name || '?').charAt(0).toUpperCase()}
              </div>
            )}
            <Typo.headingXs element="h1" fontWeight="font-medium" className="text-gray-900">
              {storefront?.name}
            </Typo.headingXs>
            <Typo.textMd fontWeight="font-medium" className="text-gray-700">
              <FormattedMessage
                id="page.storefront.closed_title"
                defaultMessage="This shop is currently closed"
              />
            </Typo.textMd>
            <Typo.textSm fontWeight="font-normal" className="text-gray-500">
              <FormattedMessage
                id="page.storefront.closed_body"
                defaultMessage="The seller has switched to a personal account. The shop may reopen later."
              />
            </Typo.textSm>
          </div>
        </PageWrapper>
      </div>
    );
  }

  const openProfile = () => {
    // if (role === ROLE_TYPES.ADMIN || role === ROLE_TYPES.SUPER_ADMIN) {
    if (!profileState) {
      dispatch(setProfileData({ userId: storefrontUser?.id, storefrontId: storefront?.id }));
      dispatch(showSlide('profile'));
    }
    // }
  };

  return (
    <div>
      <PageSeo title={storefront?.name} />
      <PageWrapper hasSpeicifWidth={true}>
        {!loading && storefront ? (
          <div className="flex flex-col gap-8">
            {isClosed && isOwner ? (
              <div className="flex flex-col items-start gap-2 rounded-xl border border-yellow-300 bg-yellow-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <Typo.textSm fontWeight="font-medium" className="text-yellow-800">
                  <FormattedMessage
                    id="page.storefront.closed_owner_banner"
                    defaultMessage="Your shop is currently closed and hidden from the marketplace."
                  />
                </Typo.textSm>
                <button
                  type="button"
                  onClick={() => navigate(ROUTES.ACCOUNT_TYPE_ROUTE)}
                  className="whitespace-nowrap text-sm font-semibold text-yellow-800 underline hover:text-yellow-900">
                  <FormattedMessage
                    id="page.storefront.closed_owner_banner_cta"
                    defaultMessage="Manage account type"
                  />
                </button>
              </div>
            ) : null}
            {/* Hero: banner carousel + shop logo, name, rating, open status, location */}
            <ImageSlider
              type="storefront"
              claimStatus={storefront?.claimStatus}
              alt={storefront?.name}
              className="w-full"
              imageUrls={storefront?.image?.map((img: Image) => img?.url)}
            />
            <div className="flex items-start gap-4">
              {storefront?.logo?.url ? (
                <img
                  src={storefront?.logo?.url}
                  alt={storefront?.name}
                  className="h-16 w-16 flex-shrink-0 rounded-full border border-gray-200 object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 text-2xl font-semibold text-yellow-700">
                  {(storefront?.name || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Typo.headingXs element="h1" fontWeight="font-medium" className="text-gray-900">
                  {storefront?.name}
                </Typo.headingXs>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                  {storefront?.category?.name ? <span>{storefront?.category?.name}</span> : null}
                  {total > 0 ? (
                    <span className="font-medium text-yellow-600">
                      ★ {Number(average).toFixed(1)}{' '}
                      <span className="font-normal text-gray-500">({total})</span>
                    </span>
                  ) : null}
                  {!isWebsiteShop ? (
                    <OperationHourLabel
                      defaultValue={storefront?.operationHours || []}
                      timeZone={storefront?.timeZone}></OperationHourLabel>
                  ) : null}
                  {storefront?.location?.address ? (
                    <Address address={storefront?.location?.address} />
                  ) : null}
                </div>
                <SocialChips
                  socials={storefrontUser?.verifiedSocials}
                  size="full"
                  className="mt-2"
                  ownerPrompt={!!user?.id && String(user.id) === String(storefrontUser?.id)}
                />
              </div>
            </div>
            {/* Shop description + actions */}
            <div className="w-full">
              {storefront?.description ? (
                <div className="max-w-[824px]">
                  <Typo.textMd
                    fontWeight="font-normal"
                    className={
                      'text-gray-500 whitespace-pre-line' + (descExpanded ? '' : ' clamp-2')
                    }>
                    {storefront?.description}
                  </Typo.textMd>
                  {storefront?.description.length > 140 ? (
                    <button
                      type="button"
                      onClick={() => setDescExpanded(!descExpanded)}
                      className="mt-1 text-sm font-semibold text-blue-600 hover:text-blue-700">
                      {descExpanded ? (
                        <FormattedMessage
                          id="page.storefront.read_less"
                          defaultMessage="Read less"
                        />
                      ) : (
                        <FormattedMessage
                          id="page.storefront.read_more"
                          defaultMessage="Read more"
                        />
                      )}
                    </button>
                  ) : null}
                </div>
              ) : null}
              {storefront?.website && sellingMode !== 'website' ? (
                <a
                  href={
                    /^https?:\/\//i.test(storefront.website)
                      ? storefront.website
                      : `https://${storefront.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex w-fit items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline">
                  <svg
                    width={16}
                    height={16}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3 12h18M12 3c2.6 2.7 2.6 15.3 0 18M12 3c-2.6 2.7-2.6 15.3 0 18" />
                  </svg>
                  {hostnameOf(storefront.website)}
                </a>
              ) : null}
              <div className="mt-5 flex flex-wrap gap-3">
                {sellingMode === 'website' && storefront?.website ? (
                  <a
                    href={
                      /^https?:\/\//i.test(storefront.website)
                        ? storefront.website
                        : `https://${storefront.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-yellow-400 px-4 py-2.5 text-sm font-semibold text-gray-900 hover:bg-yellow-500">
                    <FormattedMessage
                      id="page.storefront.shop_at"
                      defaultMessage="Shop at {domain}"
                      values={{ domain: hostnameOf(storefront.website) }}
                    />
                  </a>
                ) : null}
                {sellingMode === 'in_person' ? (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700">
                    <FormattedMessage
                      id="page.storefront.visit_in_person"
                      defaultMessage="Visit in person"
                    />
                  </span>
                ) : null}
                <Button
                  disabled={!storefront?.user?.id || user?.id == storefront?.user?.id}
                  onClick={onAskQuestion}
                  size="md"
                  theme="primary-yellow"
                  icon={<MessageIcon className="text-gray-900" />}>
                  <Typo.textSm fontWeight="font-semibold" className="text-gray-900">
                    <FormattedMessage
                      id="page.storefront.ask_question"
                      defaultMessage="Ask a question"
                    />
                  </Typo.textSm>
                </Button>
                <Button
                  disabled={user?.id == storefront?.user?.id}
                  onClick={
                    storefront?.saved_storefronts && storefront?.saved_storefronts?.length > 0
                      ? () => onSave(true, Number(storefront?.saved_storefronts[0]?.id))
                      : () => onSave(false, undefined)
                  }
                  size="md"
                  theme="base"
                  icon={
                    storefront?.saved_storefronts && storefront?.saved_storefronts?.length > 0 ? (
                      <DisLikeIcon />
                    ) : (
                      <LikeIcon />
                    )
                  }>
                  <Typo.textSm fontWeight="font-semibold" className="text-gray-700">
                    {storefront?.saved_storefronts && storefront?.saved_storefronts?.length > 0 ? (
                      <FormattedMessage id="page.storefront.saved" defaultMessage="Saved" />
                    ) : (
                      <FormattedMessage id="shared.save" defaultMessage="Save" />
                    )}
                  </Typo.textSm>
                </Button>
                <Button
                  onClick={() => onShare()}
                  size="md"
                  theme="base"
                  icon={<ShareIcon className="text-gray-700 w-5 h-5" />}>
                  <Typo.textSm fontWeight="font-semibold" className="text-gray-700">
                    <FormattedMessage id="shared.share" defaultMessage="Share" />
                  </Typo.textSm>
                </Button>
                <DirectionButton getDirection={() => onGetDirection()} />
                <ReportButton
                  disabled={
                    (storefront?.storefront_reports && storefront.storefront_reports?.length > 0) ||
                    user?.id == storefront?.user?.id
                  }
                  openReportModal={handleReportButtonClicked}
                />
                {role === ROLES.SUPER_ADMIN ? (
                  <Button
                    onClick={() => setShowWarnAndDeleteModal(true)}
                    size="md"
                    theme="base"
                    icon={<TrashIcon className="h-5 w-5 text-error-700" />}>
                    <Typo.textSm fontWeight="font-semibold" className="text-error-700">
                      <FormattedMessage id="shared.delete" defaultMessage="Delete" />
                    </Typo.textSm>
                  </Button>
                ) : null}
              </div>
            </div>
            <Divider />
            {/* Storefront Items  */}
            {storefront && <StorefrontItems storefront={storefront} onSave={save} showTools />}
            {/* Location  */}
            <div className="flex flex-col gap-6">
              <Typo.headingXs fontWeight="font-medium" className="text-gray-900">
                {isWebsiteShop ? (
                  <FormattedMessage
                    id={TRANSLATED_CONSTANTS.LOCATION.id}
                    defaultMessage={TRANSLATED_CONSTANTS.LOCATION.defaultMessage}
                  />
                ) : (
                  <FormattedMessage
                    id={TRANSLATED_CONSTANTS.MEETUP_LOCATION.id}
                    defaultMessage={TRANSLATED_CONSTANTS.MEETUP_LOCATION.defaultMessage}
                  />
                )}
              </Typo.headingXs>
              <div className="h-[400px] w-full bg-[#E2E2E2] rounded-lg">
                {storefront &&
                Number.isFinite(Number(storefront?.location?.latitude)) &&
                Number.isFinite(Number(storefront?.location?.longitude)) ? (
                  <Location className="w-full h-[400px]" storeFronts={[storefront]} />
                ) : (
                  <div className="flex h-full w-full items-center justify-center p-6 text-center">
                    <Typo.textSm fontWeight="font-normal" className="text-gray-500">
                      <FormattedMessage
                        id="page.storefront.no_map_online"
                        defaultMessage="This business is online only — there's no physical location to show. Visit their website to shop."
                      />
                    </Typo.textSm>
                  </div>
                )}
              </div>
              <div className="flex flex-col-reverse md:flex-row justify-start items-start gap-6 md:gap-16 mb-6">
                <div className="flex flex-col gap-6">
                  {isSettingUp ? (
                    <div className="flex max-w-[420px] flex-col gap-2 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4 dark:border-[#4A4533] dark:bg-[#2B2717]">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#92400E] dark:text-[#FCD34D]">
                        🛠{' '}
                        <FormattedMessage
                          id="page.storefront.setting_up_title"
                          defaultMessage="New owner — setting up"
                        />
                      </span>
                      <Typo.textSm fontWeight="font-normal" className="text-gray-600">
                        <FormattedMessage
                          id="page.storefront.setting_up_body"
                          defaultMessage="This business was recently claimed by its owner, who is still finishing their storefront. Check back soon."
                        />
                      </Typo.textSm>
                    </div>
                  ) : null}
                  {isCommunitySpot ? (
                    <div className="flex max-w-[420px] flex-col gap-2 rounded-xl border border-[#B9E8DC] bg-[#F3FBF8] p-4 dark:border-[#1F4D45] dark:bg-[#172B27]">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0E9384] dark:text-[#5FE9D0]">
                        📍{' '}
                        <FormattedMessage
                          id="page.storefront.community_title"
                          defaultMessage="Community-added spot"
                        />
                      </span>
                      <Typo.textSm fontWeight="font-normal" className="text-gray-600">
                        <FormattedMessage
                          id="page.storefront.community_body"
                          defaultMessage="A Doge Spotter reported that this business accepts Dogecoin. It hasn't been claimed by the owner yet."
                        />
                      </Typo.textSm>
                      <div className="text-xs text-gray-500">
                        {communitySpotter ? (
                          <span>
                            <FormattedMessage
                              id="page.storefront.community_spotted_by"
                              defaultMessage="Spotted by"
                            />{' '}
                            @{communitySpotter}
                          </span>
                        ) : null}
                        {communityVerifiedOn ? (
                          <span>
                            {communitySpotter ? ' · ' : ''}
                            <FormattedMessage
                              id="page.storefront.community_verified"
                              defaultMessage="Verified"
                            />{' '}
                            {communityVerifiedOn}
                          </span>
                        ) : null}
                      </div>
                      {claimDone || myPendingClaim ? (
                        <div className="mt-1 flex flex-col gap-1">
                          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#FEF2CD] px-3 py-1 text-sm font-semibold text-[#9A6A12] dark:text-[#FBE193]">
                            ⏳{' '}
                            <FormattedMessage
                              id="page.storefront.claim_pending_chip"
                              defaultMessage="Claim pending"
                            />
                          </span>
                          <Typo.textSm fontWeight="font-normal" className="text-gray-500">
                            <FormattedMessage
                              id="page.storefront.claim_pending_note"
                              defaultMessage="A DogeList admin is reviewing your claim. You'll get a notification when it's decided."
                            />
                          </Typo.textSm>
                        </div>
                      ) : claimOpen ? (
                        <div className="mt-1 flex flex-col gap-2">
                          <input
                            value={claimRole}
                            onChange={(e) => setClaimRole(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: 'page.storefront.claim_role_placeholder',
                              defaultMessage: 'Your role (owner, manager...)'
                            })}
                            className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                          />
                          <input
                            value={claimEmail}
                            onChange={(e) => setClaimEmail(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: 'page.storefront.claim_email_placeholder',
                              defaultMessage: 'Business contact email'
                            })}
                            className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                          />
                          <input
                            value={claimPhone}
                            onChange={(e) => setClaimPhone(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: 'page.storefront.claim_phone_placeholder',
                              defaultMessage: 'Business phone (optional)'
                            })}
                            className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                          />
                          <select
                            value={claimProofType}
                            onChange={(e) => setClaimProofType(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 p-2 text-sm">
                            <option value="domain_email">
                              {intl.formatMessage({
                                id: 'page.storefront.claim_proof_domain',
                                defaultMessage: "Email from the business's website domain"
                              })}
                            </option>
                            <option value="website_badge">
                              {intl.formatMessage({
                                id: 'page.storefront.claim_proof_badge',
                                defaultMessage: 'DogeList badge or link on your website'
                              })}
                            </option>
                            <option value="social_post">
                              {intl.formatMessage({
                                id: 'page.storefront.claim_proof_social',
                                defaultMessage: 'Post or DM from your official social account'
                              })}
                            </option>
                            <option value="phone_call">
                              {intl.formatMessage({
                                id: 'page.storefront.claim_proof_phone',
                                defaultMessage: 'Phone call'
                              })}
                            </option>
                            <option value="other">
                              {intl.formatMessage({
                                id: 'page.storefront.claim_proof_other',
                                defaultMessage: 'Other'
                              })}
                            </option>
                          </select>
                          <input
                            value={claimProofUrl}
                            onChange={(e) => setClaimProofUrl(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: 'page.storefront.claim_proof_url_placeholder',
                              defaultMessage: 'Proof link (page, post, or photo URL)'
                            })}
                            className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                          />
                          <textarea
                            value={claimProofNote}
                            onChange={(e) => setClaimProofNote(e.target.value)}
                            placeholder={intl.formatMessage({
                              id: 'page.storefront.claim_proof_note_placeholder',
                              defaultMessage: 'Or tell us how we can verify you (no link needed)'
                            })}
                            rows={2}
                            className="w-full rounded-lg border border-gray-300 p-2 text-sm"
                          />
                          {claimError ? (
                            <Typo.textSm fontWeight="font-medium" className="text-red-700">
                              {claimError}
                            </Typo.textSm>
                          ) : null}
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={!claimFormValid || claimBusy}
                              onClick={onClaimSubmit}
                              className="tdl-light inline-flex w-fit items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-gray-900 disabled:opacity-50"
                              style={{ backgroundColor: '#EF9F27' }}>
                              <FormattedMessage
                                id="page.storefront.claim_submit"
                                defaultMessage="Submit claim"
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => setClaimOpen(false)}
                              className="inline-flex w-fit items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700">
                              <FormattedMessage
                                id="page.storefront.claim_cancel"
                                defaultMessage="Cancel"
                              />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={onClaimClick}
                          className="mt-1 inline-flex w-fit items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-gray-900"
                          style={{ backgroundColor: '#EF9F27' }}>
                          <FormattedMessage
                            id="page.storefront.community_claim_cta"
                            defaultMessage="Claim this business"
                          />
                        </button>
                      )}
                    </div>
                  ) : (
                    <AccountInfo
                      openProfile={openProfile}
                      className=" flex justify-start gap-4 items-start"
                      name={storefrontUser?.name}
                      isOnline={isUserOnline}
                      username={storefrontUser?.username}
                      profileImage={storefrontUser?.profileImage?.url}
                    />
                  )}
                  <Button onClick={onGetDirection} theme="base" size="lg">
                    <Typo.textMd fontWeight="font-semibold" className="text-gray-700">
                      <FormattedMessage
                        id={TRANSLATED_CONSTANTS.GET_DIRECTIONS.id}
                        defaultMessage={TRANSLATED_CONSTANTS.GET_DIRECTIONS.defaultMessage}
                      />
                    </Typo.textMd>
                  </Button>
                </div>
                <div className="flex flex-col gap-2">
                  {storefront?.location?.address ? (
                    <Address address={storefront?.location.address} />
                  ) : (
                    ''
                  )}
                  <Typo.textMd fontWeight="font-normal" className="text-gray-500 max-w-[760px]">
                    {isWebsiteShop ? (
                      <FormattedMessage
                        id="page.storefront.location_website_note"
                        defaultMessage="Where this business is based. You complete your purchase on their website, not here."
                      />
                    ) : (
                      <FormattedMessage
                        id={TRANSLATED_CONSTANTS.LISTING_ADDRESS_DESCRIPTION.id}
                        defaultMessage={
                          TRANSLATED_CONSTANTS.LISTING_ADDRESS_DESCRIPTION.defaultMessage
                        }
                      />
                    )}
                  </Typo.textMd>
                </div>
              </div>
            </div>
            <Divider />
            {/* Reviews  */}
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-3">
                <Typo.headingXs fontWeight="font-medium" className="text-gray-900">
                  <FormattedMessage id="page.storefront.reviews_heading" defaultMessage="Reviews" />
                </Typo.headingXs>
                {!isOwner && (
                  <Button onClick={onLeaveReview} size="md" theme="primary-yellow">
                    <Typo.textMd
                      fontWeight="font-semibold"
                      className="whitespace-nowrap text-gray-900">
                      {myReview ? (
                        <FormattedMessage
                          id="page.storefront.edit_review"
                          defaultMessage="Edit your review"
                        />
                      ) : (
                        <FormattedMessage
                          id="page.storefront.leave_review"
                          defaultMessage="Leave a review"
                        />
                      )}
                    </Typo.textMd>
                  </Button>
                )}
              </div>
              <ReviewList
                reviews={reviews}
                rating={average}
                total={total}
                histogram={histogram}
                showLeaveCta={false}
                sellerName={storefront?.name}
              />
            </div>
            <Divider />
          </div>
        ) : (
          <Loader className=" h-screen" />
        )}
      </PageWrapper>
      <ReportModal
        onReport={onReport}
        isOpen={showReportListingModal}
        onCloseReportModal={(value: boolean) => onCloseReportModal(value)}
        type="listing"
        confirmText={TRANSLATED_CONSTANTS.REPORT_STOREFRONT_MODAL_CONFIRM_BUTTON}
        modalTitle={TRANSLATED_CONSTANTS.REPORT_STOREFRONT_MODAL_TITLE}
        dropDownLabel={intl.formatMessage({
          id: TRANSLATED_CONSTANTS.REPORT_STOREFRONT_MODAL_LABEL.id,
          defaultMessage: TRANSLATED_CONSTANTS.REPORT_STOREFRONT_MODAL_LABEL.defaultMessage
        })}
      />
      <PrimaryActionModal
        title={TRANSLATED_CONSTANTS.WARN_DELETE_LISTING_MODAL_TITLE}
        isOpen={showWarnAndDeleteModal}
        onClosePrimaryActionModal={(value: boolean) => onCloseWarnAndDeleteModal(value)}
        subTitle={TRANSLATED_CONSTANTS.WARN_DELETE_LISTING_MODAL_SUBTITLE}
        confirmText={TRANSLATED_CONSTANTS.WARN_DELETE_LISTING_MODAL_CONFIRM_BUTTON}
        icon={<TrashIcon />}
      />
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        onSubmit={onSubmitReview}
        sellerName={storefront?.name}
        existing={myReview ? { rating: myReview.rating, comment: myReview.text } : null}
        submitting={submitting}
      />
    </div>
  );
};

export default StorefrontDetails;
